// 资产评论按钮（项目资产页）与分镜评论按钮（分镜列表页）共用的视觉组件。
// 数据接口不同，但样式与「最新评论滚动预览」逻辑完全一致，统一收口在这里，避免分开维护。

// CJK/全角字符约占两个「ch」，ASCII 约占一个；预览容器以 ch 为单位，
// 用 .length 数字符个数会把中文严重低估，导致溢出却不滚动。
export function commentVisualWidth(text: string): number {
  let w = 0;
  for (const ch of text) w += ch.charCodeAt(0) > 0xff ? 2 : 1;
  return w;
}

let marqueeInjected = false;
export function injectMarquee() {
  if (marqueeInjected) return;
  marqueeInjected = true;
  const s = document.createElement('style');
  s.textContent = `@keyframes comment-preview-scroll{0%{transform:translateX(0)}100%{transform:translateX(-100%)}}`;
  document.head.appendChild(s);
}

export type CommentPreviewSize = 'sm' | 'md';

// 唯一的尺寸差异：分镜在大号操作栏里（md），资产在紧凑表头里（sm）。其余样式完全共用。
const SIZES: Record<CommentPreviewSize, {
  height: string; font: string; labelPad: string; plainPad: string;
  badgeFont: string; containerCh: number;
}> = {
  sm: { height: '24px', font: '0.75rem', labelPad: '0 0.6rem', plainPad: '0 0.75rem', badgeFont: '0.7rem', containerCh: 18 },
  md: { height: '36px', font: '0.85rem', labelPad: '0 0.75rem', plainPad: '0 1.25rem', badgeFont: '0.78rem', containerCh: 20 },
};

interface CommentPreviewButtonProps {
  /** 未处理评论数；<=0 时显示普通「评论」按钮 */
  count: number;
  /** 最新一条未处理评论的文本，用于滚动预览（可为空） */
  latestComment?: string;
  /** 有未处理评论时的标签，如「查看新评论」 */
  viewNewLabel: string;
  /** 无未处理评论时的标签，如「评论」 */
  buttonLabel: string;
  onClick: () => void;
  size?: CommentPreviewSize;
  /** 仅在有未处理评论的形态上挂 data-testid */
  testId?: string;
}

export default function CommentPreviewButton({
  count, latestComment = '', viewNewLabel, buttonLabel, onClick, size = 'md', testId,
}: CommentPreviewButtonProps) {
  const s = SIZES[size];
  const previewWidth = `${s.containerCh}ch`;

  if (count <= 0) {
    return (
      <button
        onClick={onClick}
        title={buttonLabel}
        style={{
          background: '#fef3c7', color: '#000', border: '1px solid #fde68a', borderRadius: '8px',
          fontSize: s.font, cursor: 'pointer', padding: s.plainPad, height: s.height,
          fontWeight: 500, display: 'inline-flex', alignItems: 'center', flexShrink: 0,
        }}
      >
        {buttonLabel}
      </button>
    );
  }

  const text = latestComment;
  const visualWidth = commentVisualWidth(text);
  // 预览容器减去左右内边距后的可视宽度约 containerCh-2；超出即滚动。
  const needsScroll = visualWidth > s.containerCh - 2;
  if (needsScroll) injectMarquee();

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      title={viewNewLabel}
      style={{
        display: 'flex', alignItems: 'center', gap: 0,
        background: '#fef3c7', color: '#000', border: '1px solid #fde68a', borderRadius: '8px',
        fontSize: s.font, cursor: 'pointer', padding: 0, overflow: 'hidden',
        flexShrink: 0, height: s.height,
      }}
    >
      <span style={{
        padding: s.labelPad, whiteSpace: 'nowrap', fontWeight: 600, height: '100%',
        borderRight: text ? '1px solid #fde68a' : 'none',
        display: 'flex', alignItems: 'center', gap: '0.3rem',
      }}>
        {viewNewLabel}
        <span style={{
          background: '#ef4444', color: '#fff', fontSize: s.badgeFont, fontWeight: 700,
          padding: '0 0.4rem', borderRadius: '9999px', lineHeight: '1.4', minWidth: '1.2rem', textAlign: 'center',
        }}>
          {count}
        </span>
      </span>
      {text && (
        <span style={{
          width: previewWidth, overflow: 'hidden', padding: '0 0.5rem',
          display: 'flex', alignItems: 'center',
        }}>
          {needsScroll ? (
            <span style={{
              display: 'inline-block', whiteSpace: 'nowrap', paddingLeft: previewWidth,
              animation: `comment-preview-scroll ${Math.max(visualWidth * 0.3, 4)}s linear infinite`,
            }}>
              {text}
            </span>
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
          )}
        </span>
      )}
    </button>
  );
}
